import { HttpErrorResponse } from '@angular/common/http';
import type { Signal } from '@angular/core';
import { Component, computed, DestroyRef , inject, input, output, signal, untracked } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { ButtonComponent } from '@rpgc-ai/ui/button/button.component';
import { CheckboxComponent } from '@rpgc-ai/ui/checkbox/checkbox.component';
import { EditableDivComponent } from '@rpgc-ai/ui/editable-div/editable-div.component';
import { ScrollbarDirective } from '@rpgc-ai/ui/scrollbar/scrollbar.directive';

import { ReportCharacterService } from '@rpgc-ai/services/character/report-character.service';
import { ChatMessageService } from '@rpgc-ai/services/chat/chat-message.service';
import { ChatbotCommentsService } from '@rpgc-ai/services/comments/chatbot-comments.service';
import { ModalService } from '@rpgc-ai/services/modal/modal.service';
import { NotificationsService } from '@rpgc-ai/services/notifications/notifications.service';
import { ProfileReportService } from '@rpgc-ai/services/public-profile/profile-report.service';
import { TranslateBackendService } from '@rpgc-ai/services/translate-backend/translate-backend.service';

import type { IComplainForm, IComplaintResult } from '@rpgc-ai/shared-interfaces/interfaces/complain.interface';
import { COMPLAIN_CHECKBOX_LABELS, EComplainCheckbox } from '@rpgc-ai/shared-interfaces/interfaces/complain.interface';
import { MODAL_COMPONENT, Padding } from '@rpgc-ai/shared-interfaces/tokens/modal.token';

import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { finalize, take } from 'rxjs';

export enum EModalType {
  MESSAGE_REPORT = 'report-message',
  PROFILE_REPORT = 'report-profile',
  CHATBOT_REPORT = 'report-chatbot',
  CHATBOT_COMMENT = 'chatbot-comment',
}

type TReportService = ChatMessageService | ProfileReportService | ReportCharacterService | null;

@Component({
  selector: 'modal-complain',
  imports: [ButtonComponent, CheckboxComponent, ReactiveFormsModule, ScrollbarDirective, EditableDivComponent, TranslocoPipe],
  templateUrl: './complain.component.html',
  styleUrl: './styles/complain.component.scss',
})
export class ComplainComponent {
  readonly #destroyRef = inject(DestroyRef);

  readonly id = input<string | null>(null);
  readonly rating = input<string | null>(null);
  readonly modalType = input<EModalType | null>(null);

  /** Ссылка на родительский компонент модального окна */
  readonly #modal = inject(MODAL_COMPONENT, { optional: true });
  readonly #modalService = inject(ModalService);
  readonly #chatMessageService = inject(ChatMessageService);
  readonly #profileReportService = inject(ProfileReportService);
  readonly #reportCharacterService = inject(ReportCharacterService);
  readonly #chatbotCommentsService = inject(ChatbotCommentsService);
  readonly #notificationsService = inject(NotificationsService);
  readonly #translocoService = inject(TranslocoService);
  readonly #translateBackendService = inject(TranslateBackendService);

  readonly #currentService = computed<TReportService>(() => {
    switch (this.modalType()) {
      case EModalType.MESSAGE_REPORT:
        return this.#chatMessageService;
      case EModalType.PROFILE_REPORT:
        return this.#profileReportService;
      case EModalType.CHATBOT_REPORT:
        return this.#reportCharacterService;
      case EModalType.CHATBOT_COMMENT:
        return null;
      default:
        return null;
    }
  });

  /** Сигнал для результата формы */
  readonly submitted = output<IComplaintResult>();

  /** Сигнал для отслеживания состояния отправки */
  readonly #isSubmitting = signal<boolean>(false);
  readonly isSubmitting = this.#isSubmitting.asReadonly();

  /** Сигнал для отслеживания ошибок */
  readonly #hasError = signal<boolean>(false);
  readonly hasError = this.#hasError.asReadonly();

  /** Мемоизированный массив с элементами чекбоксов */
  readonly #checkboxItems = signal(
    Object.values(EComplainCheckbox).map((id) => ({
      id,
      label: COMPLAIN_CHECKBOX_LABELS[id],
    })),
  );

  readonly checkboxItems: Signal<Array<{ id: EComplainCheckbox; label: string }>> = this.#checkboxItems.asReadonly();

  readonly descriptionMaxLength = 500;

  /** Форма для сбора жалобы */
  readonly complainForm = new FormGroup<IComplainForm>(
    (() => {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      const checkboxControls = {} as Record<EComplainCheckbox, FormControl<boolean>>;

      for (const checkbox of Object.values(EComplainCheckbox)) {
        checkboxControls[checkbox] = new FormControl<boolean>(false, { nonNullable: true });
      }

      return {
        ...checkboxControls,
        details: new FormControl<string>('', {
          validators: [Validators.maxLength(this.descriptionMaxLength)],
          nonNullable: true,
        }),
      };
    })(),
  );

  /** Сигнал со значениями формы */
  readonly #formValue = toSignal(this.complainForm.valueChanges, {
    initialValue: this.complainForm.getRawValue(),
  });

  readonly checkboxCount = computed<number>(() => {
    const form = this.#formValue();
    if (!form) {
      return 0;
    }
    return Object.values(form).filter((v) => v === true).length;
  });

  /** Сигнал, показывающий, пуста ли форма */
  readonly isFormEmpty = computed<boolean>(() => {
    const formValue = this.#formValue();

    return untracked(() => {
      if (!formValue) {
        return true;
      }

      // Проверяем отмеченные чекбоксы
      return !Object.values(EComplainCheckbox).some((checkbox) => formValue[checkbox as keyof typeof formValue] === true);
    });
  });

  constructor() {
    this.#modal?.paddingStyle?.set(Padding.HIDE);
  }

  /**
   * Отправляет форму и эмитит событие с результатом
   */
  submitForm(): void {
    // Проверяем валидность формы
    if (this.complainForm.invalid) {
      this.complainForm.markAllAsTouched();
      return;
    }

    const id = this.id();

    if (!id) {
      return;
    }

    if (this.modalType() === EModalType.CHATBOT_COMMENT) {
      this.#isSubmitting.set(true);
      this.#hasError.set(false);
      this.#submitCommentReport(id);
      return;
    }

    const service = this.#currentService();

    if (!service) {
      return;
    }

    try {
      // Устанавливаем состояние отправки
      this.#isSubmitting.set(true);
      this.#hasError.set(false);
      const report = this.#assembleReport(service);
      const rating = +(this.rating() || '');
      if (!report) {
        return;
      }

      service.setReportBody({ id, report });

      if (rating) {
        //change rating if new provided
        this.#chatMessageService.rateMessageRequestBody.set({ messageId: id, rating: { ratingValue: +rating } });
      }

      this.closeForm();
    } catch (error) {
      // Обрабатываем ошибки
      // eslint-disable-next-line no-console
      console.error('Error submitting complain form:', error);
      this.#hasError.set(true);
    } finally {
      // Сбрасываем состояние отправки
      this.#isSubmitting.set(false);
    }
  }

  /**
   * Сбрасывает форму в исходное состояние
   */
  closeForm(): void {
    this.complainForm.reset({ details: '' });
    if (this.#modal) {
      this.#modal.close();
    }
  }

  /**
   * Сбрасывает форму в исходное состояние
   * и возвращает предыдущую модалку
   */
  cancelForm(): void {
    this.complainForm.reset({ details: '' });
    void this.#modalService.back();
  }

  /**
   * Устанавливает значение чекбокса
   * @param id Идентификатор чекбокса
   * @param checked Новое значение
   */
  setCheckboxValue(id: EComplainCheckbox, checked: boolean): void {
    const control = this.complainForm.get(id);
    if (control) {
      control.setValue(checked, { emitEvent: true });
      control.markAsTouched();
    }
  }

  #assembleReport(service: TReportService): IComplaintResult | null {
    if (!service) {
      return null;
    }
    // Используем значение из сигнала для оптимизации
    const formValue = this.#formValue();
    // Фильтруем только отмеченные чекбоксы и возвращаем массив enum-значений
    const reason = Object.values(EComplainCheckbox).filter((checkbox) => formValue[checkbox as keyof typeof formValue] === true);
    const details = formValue.details?.trim() || '';
    let report: IComplaintResult = {};

    // Отправляем результат и сбрасываем форму
    if (service instanceof ChatMessageService) {
      report = {
        reason,
        complaintDetails: details,
      };
    } else if (service instanceof ProfileReportService) {
      report = {
        complaintReason: reason,
        complaintDetails: details,
      };
    } else if (service instanceof ReportCharacterService) {
      report = {
        reason,
        details,
      };
    }
    return report;
  }

  #submitCommentReport(id: string): void {
    const formValue = this.#formValue();
    const reason = Object.values(EComplainCheckbox).filter((checkbox) => formValue[checkbox as keyof typeof formValue] === true);
    const details = formValue.details?.trim() || '';

    this.#chatbotCommentsService
      .reportComment({ id, reason, details })
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        take(1),
        finalize(() => this.#isSubmitting.set(false)),
      )
      .subscribe({
        next: () => {
          this.#notificationsService.addSuccessNotification(
            this.#translocoService.translate('NOTIFICATIONS.CHAT_MESSAGE.REPORT_MESSAGE_SENT'),
          );
          this.closeForm();
        },
        error: (error: unknown) => {
          this.#hasError.set(true);
          this.#notificationsService.addErrorNotification(this.#getCommentReportErrorMessage(error));
        },
      });
  }

  #getCommentReportErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse && error.status === 409) {
      const message = typeof error.error?.message === 'string' ? error.error.message : 'You have already reported this comment';

      return this.#translateBackendService.backendMessageToKey(message);
    }

    return this.#translocoService.translate('NOTIFICATIONS.CHAT_MESSAGE.ERROR');
  }
}
